import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { fetchProduct } from "../redux/slice/Product";
import { addToCart } from "../redux/slice/CartSlice";
import { trackAddToCart } from "../analytics/events";
import Loader from "../components/Loader";
import noimage from "../image/noimage.jpg";
import weightliftingImg from "../image/weightlifting.png";
import dumbelImg from "../image/dumbel.png";
import muscleImg from "../image/muscle.png";

const carouselResponsive = {
  superLargeDesktop: { breakpoint: { max: 4000, min: 1921 }, items: 4 },
  desktop:           { breakpoint: { max: 1920, min: 1025 }, items: 4 },
  tablet:            { breakpoint: { max: 1024, min: 768  }, items: 2 },
  mobile:            { breakpoint: { max: 576,  min: 0    }, items: 2 },
};

const heroSlides = [
  {
    eyebrow: "🔥 Best Sellers 2025",
    heading: "Shop Premium",
    highlight: "Gym Equipment",
    sub: "Dumbbells, Barbells, Benches & Cardio Machines — Delivered Pan-India",
    ctaPrimary: { label: "Shop Now", href: "/shop/fitness-equipment" },
    ctaSecondary: { label: "Browse Categories", href: "#shop-categories" },
    image: weightliftingImg,
    bg: "linear-gradient(135deg, #0f172a 0%, #1a2744 60%, #1e293b 100%)",
    accentColor: "#e11d48",
  },
  {
    eyebrow: "🏠 Home Gym Special",
    heading: "Build Your",
    highlight: "Dream Home Gym",
    sub: "Complete home gym setups starting ₹2,999. Train harder, train smarter — from anywhere.",
    ctaPrimary: { label: "Explore Home Gym", href: "/shop/fitness-equipment" },
    ctaSecondary: { label: "View Deals", href: "/shop/fitness-equipment" },
    image: dumbelImg,
    bg: "linear-gradient(135deg, #1a0533 0%, #2d1b69 60%, #1e1b4b 100%)",
    accentColor: "#a855f7",
  },
  {
    eyebrow: "💪 Nutrition & Supplements",
    heading: "Fuel Your",
    highlight: "Performance",
    sub: "Premium protein powders, vitamins & nutrition essentials from trusted fitness brands.",
    ctaPrimary: { label: "Shop Supplements", href: "/shop/fitness-equipment" },
    ctaSecondary: { label: "See All Products", href: "/shop/fitness-equipment" },
    image: muscleImg,
    bg: "linear-gradient(135deg, #0c2340 0%, #0f3d6b 60%, #1e3a5f 100%)",
    accentColor: "#0ea5e9",
  },
];

const trustItems = [
  { icon: "bi bi-truck",         title: "Free Delivery",      desc: "On orders above ₹999"      },
  { icon: "bi bi-arrow-repeat",  title: "Easy Returns",       desc: "7-day hassle-free returns"  },
  { icon: "bi bi-shield-check",  title: "Secure Payments",    desc: "100% safe & encrypted"      },
  { icon: "bi bi-patch-check",   title: "Genuine Products",   desc: "Quality guaranteed"         },
  { icon: "bi bi-headset",       title: "Expert Support",     desc: "Fitness team available"     },
];

const whyItems = [
  { icon: "bi bi-award-fill",         stat: "10,000+",  title: "Products Sold",       desc: "Trusted by thousands of gym enthusiasts across India.",          color: "#e11d48", iconBg: "#fff1f2" },
  { icon: "bi bi-tags-fill",          stat: "30%",      title: "Avg. Savings",        desc: "Competitive pricing with regular deals and member discounts.",    color: "#f59e0b", iconBg: "#fffbeb" },
  { icon: "bi bi-headset",            stat: "24/7",     title: "Expert Support",      desc: "Fitness experts help you choose the right gear for your goals.", color: "#10b981", iconBg: "#f0fdf4" },
  { icon: "bi bi-truck",              stat: "2-5 Days", title: "Pan-India Delivery",  desc: "Fast, reliable shipping with real-time tracking nationwide.",    color: "#6366f1", iconBg: "#eef2ff" },
  { icon: "bi bi-arrow-repeat",       stat: "7 Days",   title: "Easy Returns",        desc: "Hassle-free returns policy — no questions asked.",               color: "#0ea5e9", iconBg: "#f0f9ff" },
  { icon: "bi bi-patch-check-fill",   stat: "100%",     title: "Genuine Products",    desc: "Every product is hand-picked and quality tested for durability.", color: "#8b5cf6", iconBg: "#f5f3ff" },
];

export default function Product() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [search, setSearch]       = useState("");
  const [addedKey, setAddedKey]   = useState(null);
  const [slide, setSlide]         = useState(0);
  const [paused, setPaused]       = useState(false);

  const { productCategory, bestProduct, trendingProduct, categoryLoading, bestProductLoading, trendingProductLoading, categoryError, bestProductError, trendingProductError } =
    useSelector((state) => state.Product);

  useEffect(() => {
    dispatch(fetchProduct());
    if (categoryError || bestProductError || trendingProductError) navigate("/error");
  }, [dispatch, navigate, categoryError, bestProductError, trendingProductError]);

  // Auto-advance hero slider
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % heroSlides.length), 5000);
    return () => clearInterval(t);
  }, [paused]);

  const prevSlide = useCallback(() => setSlide((s) => (s - 1 + heroSlides.length) % heroSlides.length), []);
  const nextSlide = useCallback(() => setSlide((s) => (s + 1) % heroSlides.length), []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/shop/all?search=${encodeURIComponent(search.trim())}`);
  };

  const handleQuickAdd = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    const firstVariant = product.variants?.[0] || null;
    dispatch(addToCart({ product, variant: firstVariant, quantity: 1 }));
    trackAddToCart(product, firstVariant, 1);
    setAddedKey(product.id);
    setTimeout(() => setAddedKey(null), 1800);
  };

  const ProductCard = ({ item }) => {
    const hasDiscount = Number(item.price) > Number(item.discount_price);
    const discPct = hasDiscount ? Math.round(((item.price - item.discount_price) / item.price) * 100) : 0;
    return (
      <Link to={`/shop/${item.category?.slug || "fitness-equipment"}/${item.slug}`} className="mg-pcard">
        <div className="mg-pcard-img-wrap">
          {discPct > 0 && <span className="mg-pcard-badge-disc">-{discPct}%</span>}
          <img src={item.thumbnail || noimage} alt={item.title} onError={(e) => { e.target.src = noimage; }} className="mg-pcard-img" />
          <button className={`mg-pcard-add-btn${addedKey === item.id ? " added" : ""}`} onClick={(e) => handleQuickAdd(e, item)} aria-label="Add to cart">
            {addedKey === item.id ? <><i className="bi bi-check-lg"></i> Added</> : <><i className="bi bi-cart-plus"></i> Add to Cart</>}
          </button>
        </div>
        <div className="mg-pcard-body">
          <span className="mg-pcard-cat">{item.category?.title}</span>
          <h3 className="mg-pcard-title">{item.title}</h3>
          <div className="mg-pcard-price-row">
            <span className="mg-pcard-sale">₹{Number(item.discount_price).toLocaleString("en-IN")}</span>
            {hasDiscount && <span className="mg-pcard-mrp">₹{Number(item.price).toLocaleString("en-IN")}</span>}
          </div>
        </div>
      </Link>
    );
  };

  const currentSlide = heroSlides[slide];

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Buy Gym Equipment, Accessories &amp; Supplements Online | MeraGym Shop</title>
          <meta name="description" content="Shop premium gym equipment, protein supplements, and fitness accessories online at MeraGym. Best prices, pan-India delivery, easy returns, and genuine products guaranteed." />
          <meta name="keywords" content="buy gym equipment online, gym equipment India, dumbbells online, fitness accessories, protein supplements, home gym setup, gym machines price, buy treadmill online, gym bench, barbell set India" />
          <link rel="canonical" href="https://meragym.com/shop" />
          <meta property="og:title" content="MeraGym Shop — Premium Gym Equipment & Supplements" />
          <meta property="og:description" content="Shop premium gym equipment and supplements online. Fast pan-India shipping, easy 7-day returns, genuine products." />
          <meta property="og:url" content="https://meragym.com/shop" />
          <meta property="og:type" content="website" />
          <meta property="og:image" content="https://meragym.com/og-shop.jpg" />
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "MeraGym Shop — Gym Equipment & Supplements",
            "description": "Buy premium gym equipment, supplements, and fitness accessories online at MeraGym.",
            "url": "https://meragym.com/shop",
            "numberOfItems": trendingProduct.length + bestProduct.length,
            "itemListElement": [...trendingProduct, ...bestProduct].slice(0, 10).map((p, i) => ({
              "@type": "ListItem",
              "position": i + 1,
              "name": p.title,
              "url": `https://meragym.com/shop/${p.category?.slug || "fitness-equipment"}/${p.slug}`,
              "image": p.thumbnail,
              "offers": {
                "@type": "Offer",
                "price": p.discount_price,
                "priceCurrency": "INR",
                "availability": "https://schema.org/InStock"
              }
            }))
          })}</script>
        </Helmet>
      </HelmetProvider>

      {/* ── Hero Slider ── */}
      <section
        className="shop-hero-slider"
        style={{ background: currentSlide.bg }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Slide content */}
        {heroSlides.map((s, i) => (
          <div key={i} className={`shop-hero-slide${slide === i ? " active" : ""}`}>
            <div className="container">
              <div className="shop-hero-inner">
                <div className="shop-hero-text">
                  <span className="shop-hero-eyebrow" style={{ background: `${s.accentColor}22`, color: s.accentColor, borderColor: `${s.accentColor}44` }}>
                    {s.eyebrow}
                  </span>
                  <h1 className="shop-hero-heading">
                    {s.heading}<br />
                    <span className="shop-hero-highlight" style={{ color: s.accentColor }}>{s.highlight}</span>
                  </h1>
                  <p className="shop-hero-sub">{s.sub}</p>
                  <form className="shop-hero-search" onSubmit={handleSearch}>
                    <i className="bi bi-search"></i>
                    <input type="text" placeholder="Search gym products..." value={search} onChange={(e) => setSearch(e.target.value)} />
                    <button type="submit">Search</button>
                  </form>
                  <div className="shop-hero-actions">
                    <Link to={s.ctaPrimary.href} className="shop-hero-btn-primary" style={{ background: s.accentColor }}>
                      {s.ctaPrimary.label} <i className="bi bi-arrow-right ms-2"></i>
                    </Link>
                    <Link to={s.ctaSecondary.href} className="shop-hero-btn-ghost">
                      {s.ctaSecondary.label}
                    </Link>
                  </div>
                </div>
                <div className="shop-hero-img-wrap">
                  <img src={s.image} alt={s.highlight} className="shop-hero-product-img" />
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Arrows */}
        <button className="shop-hero-arrow left" onClick={prevSlide} aria-label="Previous slide">
          <i className="bi bi-chevron-left"></i>
        </button>
        <button className="shop-hero-arrow right" onClick={nextSlide} aria-label="Next slide">
          <i className="bi bi-chevron-right"></i>
        </button>

        {/* Dots */}
        <div className="shop-hero-dots">
          {heroSlides.map((_, i) => (
            <button
              key={i}
              className={`shop-hero-dot${slide === i ? " active" : ""}`}
              onClick={() => setSlide(i)}
              aria-label={`Slide ${i + 1}`}
              style={slide === i ? { background: currentSlide.accentColor } : {}}
            />
          ))}
        </div>

        {/* Progress bar */}
        <div className="shop-hero-progress" style={{ background: currentSlide.accentColor }}
          key={`${slide}-${paused}`}
        ></div>
      </section>

      {/* ── Trust Bar ── */}
      <section className="shop-trust-bar">
        <div className="container">
          <div className="shop-trust-items">
            {trustItems.map((t, i) => (
              <div className="shop-trust-item" key={i}>
                <i className={t.icon}></i>
                <div>
                  <strong>{t.title}</strong>
                  <span>{t.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Shop by Category ── */}
      <section className="shop-cats-section" id="shop-categories">
        <div className="container">
          <div className="shop-section-head">
            <div>
              <h2>Shop by Category</h2>
              <p>Find equipment that matches your fitness goals</p>
            </div>
            <Link to="/shop/fitness-equipment" className="shop-see-all">
              All Categories <i className="bi bi-arrow-right-circle-fill ms-1"></i>
            </Link>
          </div>
          <div className="shop-cats-grid">
            {categoryLoading ? <Loader /> : productCategory.map((data) => (
              <Link to={`/shop/${data.slug}`} key={data.id} className="shop-cat-card">
                <img src={data.image} alt={`${data.title} — Buy Online`} className="shop-cat-img" />
                <div className="shop-cat-overlay">
                  <h3 className="shop-cat-name">{data.title}</h3>
                  <span className="shop-cat-cta">Shop Now <i className="bi bi-arrow-right ms-1"></i></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Promo Banner ── */}
      <section className="shop-promo-banner">
        <div className="container">
          <div className="shop-promo-inner">
            <div className="shop-promo-text">
              <span className="shop-promo-tag">⚡ Limited Time Offer</span>
              <h2 className="shop-promo-heading">Up to <span>40% Off</span> on Fitness Accessories</h2>
              <p>Resistance bands, gloves, belts, straps & more — grab the best deals before they're gone.</p>
              <Link to="/shop/fitness-equipment" className="shop-promo-btn">
                Grab the Deal <i className="bi bi-arrow-right ms-2"></i>
              </Link>
            </div>
            <div className="shop-promo-img-wrap">
              <img src={dumbelImg} alt="Fitness equipment deals" className="shop-promo-img" />
            </div>
          </div>
        </div>
      </section>

      {/* ── Trending Products ── */}
      <section className="shop-products-section">
        <div className="container">
          <div className="shop-section-head">
            <div>
              <h2>Trending Products</h2>
              <p>Most loved by fitness enthusiasts this week</p>
            </div>
            <Link className="shop-see-all" to="/shop/fitness-equipment">
              See All <i className="bi bi-arrow-right-circle-fill ms-1"></i>
            </Link>
          </div>
          {trendingProductLoading ? <Loader /> : (
            <div className="shop-carousel-wrap">
              <Carousel responsive={carouselResponsive} swipeable draggable infinite showDots={false} autoPlay={false} itemClass="shop-carousel-item">
                {trendingProduct.map((item) => <ProductCard item={item} key={item.id} />)}
              </Carousel>
            </div>
          )}

          {/* ── Best Selling ── */}
          <div className="shop-section-head mt-5">
            <div>
              <h2>Best Selling Products</h2>
              <p>Top-rated gear trusted by our customers</p>
            </div>
            <Link className="shop-see-all" to="/shop/fitness-equipment">
              See All <i className="bi bi-arrow-right-circle-fill ms-1"></i>
            </Link>
          </div>
          {bestProductLoading ? <Loader /> : (
            <div className="shop-carousel-wrap">
              <Carousel responsive={carouselResponsive} swipeable draggable infinite showDots={false} autoPlay={false} itemClass="shop-carousel-item">
                {bestProduct.map((item) => <ProductCard item={item} key={item.id} />)}
              </Carousel>
            </div>
          )}
        </div>
      </section>

      {/* ── Why Shop With Us ── */}
      <section className="shop-why-section">
        <div className="container">
          <div className="shop-why-header">
            <h2>Why Shop at MeraGym?</h2>
            <p>India's most trusted online fitness equipment store</p>
          </div>
          <div className="shop-why-grid">
            {whyItems.map((w, i) => (
              <div key={i} className="shop-why-card" style={{ "--why-color": w.color, "--why-icon-bg": w.iconBg }}>
                <div className="shop-why-icon"><i className={w.icon}></i></div>
                <div className="shop-why-stat">{w.stat}</div>
                <h3 className="shop-why-title">{w.title}</h3>
                <p className="shop-why-desc">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SEO Content Block ── */}
      <section className="shop-seo-block">
        <div className="container">
          <div className="shop-seo-inner">
            <h2>Buy Gym Equipment Online in India — MeraGym Shop</h2>
            <p>
              MeraGym is India's trusted online destination for premium gym equipment, fitness accessories, and nutrition supplements. Whether you're setting up a commercial gym, a home workout space, or looking to upgrade your fitness gear — we've got everything you need at the best prices.
            </p>
            <p>
              Browse our wide selection of <Link to="/shop/fitness-equipment">dumbbells</Link>, barbells, weight plates, gym benches, treadmills, resistance bands, protein supplements, and more. All products are sourced from verified manufacturers and come with quality guarantees. With pan-India delivery, easy 7-day returns, and expert fitness support, shopping for gym equipment has never been easier.
            </p>
            <div className="shop-seo-tags">
              {["Dumbbells Online", "Gym Bench", "Treadmill Price", "Resistance Bands", "Protein Powder", "Barbell Set", "Home Gym Setup", "Gym Gloves", "Knee Wraps", "Gym Bag"].map((tag, i) => (
                <Link key={i} to="/shop/fitness-equipment" className="shop-seo-tag">{tag}</Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section className="shop-bottom-cta">
        <div className="container">
          <div className="shop-bottom-cta-inner">
            <h2>Ready to Build Your Perfect Gym?</h2>
            <p>Explore 500+ fitness products. Pan-India delivery. Best prices guaranteed.</p>
            <div className="shop-bottom-cta-actions">
              <Link to="/shop/fitness-equipment" className="shop-cta-btn-primary">
                <i className="bi bi-bag-fill me-2"></i>Browse All Products
              </Link>
              <Link to="/contact-us" className="shop-cta-btn-outline">
                <i className="bi bi-headset me-2"></i>Need Help Choosing?
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
